import { Component, Prop, h, Host } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-advantages';

/**
 * Two-column benefits section for SignNow integration pages.
 * Left column: optional label, required heading, and optional illustration; right column: optional intro text and a two-column checklist grid.
 * Stacks to a single column on mobile and tablet; side-by-side layout activates on desktop.
 *
 * @product signnow
 * @source advantages
 * @version 1.0.0
 * @category SignNow
 * @contentConfig Benefits section with two-column layout, heading, illustration, and checklist items
 * @slot label - <me-tagline> - Section label (e.g. "Benefits")
 * @slot heading - {required}, <me-heading tag="h2"> - Section heading
 * @slot image - <me-picture> - Illustration displayed below the heading
 * @slot intro - <me-text> - Introductory text above the checklist
 * @example
 *   <sn-advantages
 *     items='[{"title":"Save time with reusable templates","text":"Create templates of your most frequently used documents and send them for signature."},{"title":"Sync your accounts","text":"Import documents from airSlate SignNow and vice versa."}]'
 *   >
 *     <me-tagline slot="label">Benefits</me-tagline>
 *     <me-heading slot="heading" variant="2" tag="h2">A smarter way to sign and manage documents</me-heading>
 *     <me-picture slot="image" src-set="https://marketing-static.signnow.com/386/images/integrations/ilustration-violet-man.svg" alt="Author contracts"></me-picture>
 *     <me-text slot="intro">Streamline the way your team handles documents with SignNow.</me-text>
 *   </sn-advantages>
 */
@Component({
  tag: 'sn-advantages',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNAdvantages {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Checklist items displayed in the secondary column.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Bold item heading
   * @item.property {string} text - Item description text
   * @item.required title
   * @item.required text
   * @item.content title text
   * @minItems 1
   */
  @Prop({ mutable: true }) items!: any[] | string;

  componentWillLoad() {
    this.items = parseJsonProp(this.items, []);
  }

  render() {
    const items = Array.isArray(this.items) ? (this.items as Array<{ title: string; text: string }>) : [];

    return (
      <Host data-color-mode={this.colorMode}>
        <me-section class={COMPONENT_NAME} yGap="2xl" yGapLg="5xl">
          <slot name="label"></slot>
          <div class={`${COMPONENT_NAME}__wrap`}>
            <div class={`${COMPONENT_NAME}__primary`}>
              <slot name="heading"></slot>
              <slot name="image"></slot>
            </div>
            <div class={`${COMPONENT_NAME}__secondary`}>
              <slot name="intro"></slot>
              <ul class={`${COMPONENT_NAME}__list`}>
                {items.map((item) => (
                  <li class={`${COMPONENT_NAME}__item`}>
                    <h3 class={`${COMPONENT_NAME}__item-title`}>{item.title}</h3>
                    <p>{item.text}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </me-section>
      </Host>
    );
  }
}
